import { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { mealPlanAPI } from '../utils/api';
import { getTodayString } from '../utils/dateUtils';
import { recordSwapTasteSignals } from '../utils/tasteSignals';
import { resolvePairSwapContext, pairSwapErrorMessage } from './pairSwapLogic';
import { annotatePairCandidates, conflictText } from '../utils/mealConflicts';

// Conflict chip colours: allergen red, a preference conflict amber, a
// same-day repeat neutral.
function conflictChipClass(tone) {
  if (tone === 'danger') return 'bg-red-500/15 text-red-600';
  if (tone === 'info') return 'bg-secondary text-muted-foreground';
  return 'bg-amber-500/15 text-amber-600';
}

// Shared pair-safe "Replace Both" dialog used by BOTH PlanningHub and Home so
// there is a single implementation that always calls the pair-safe
// mealPlanAPI.swapPair backend route.
//
// Props:
//   context: null | { groupId, planId, date, pairRows }  — opening a non-null
//            context opens the dialog and loads candidates. `pairRows` is the
//            caller's known rows for this leftover group (used to seed cook /
//            leftover before the backend returns authoritative dates).
//   onClose():   close request (Cancel / outside click / after a swap)
//   onSwapped(): called after a successful swap so the caller can refresh.
function formatDate(dateStr) {
  // Use noon to avoid timezone issues with date-only strings.
  var d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function PairSwapDialog({ context, onClose, onSwapped }) {
  var [state, setState] = useState(null);
  var opRef = useRef(null);

  useEffect(function () {
    if (!context) { setState(null); return undefined; }
    var cancelled = false;

    var pairRows = context.pairRows || [];
    var resolved = resolvePairSwapContext(pairRows, context);
    var cookPlanId = resolved.planId;
    var authCookDate = resolved.cookDate;

    setState({
      groupId: resolved.groupId, planId: cookPlanId,
      cookDate: resolved.cookDate,
      leftoverDate: resolved.leftoverDate,
      currentMealId: resolved.currentMealId,
      candidates: [], loading: true, submitting: false, valid: true, error: null,
    });

    (async function () {
      // Validate the group via backend (never present a swap for a malformed
      // group) AND read the AUTHORITATIVE pair dates (cook first, leftover
      // second) regardless of which card opened it.
      try {
        var ctxRes = await mealPlanAPI.getMealPlanContext(cookPlanId);
        if (cancelled) return;
        if (ctxRes.data && ctxRes.data.group_valid === false) {
          setState(function (prev) { return prev && Object.assign({}, prev, { loading: false, valid: false, error: 'invalid_group' }); });
          return;
        }
        if (ctxRes.data && ctxRes.data.cook_date) {
          authCookDate = ctxRes.data.cook_date;
          setState(function (prev) { return prev && Object.assign({}, prev, {
            cookDate: ctxRes.data.cook_date,
            leftoverDate: ctxRes.data.leftover_date || prev.leftoverDate,
          }); });
        }
      } catch (err) { /* fall through — backend swap re-validates */ }
      try {
        // Ranked candidates come from the backend pair-aware ranker (regeneration
        // in suggestion mode): both affected days + per-day scale factors, scored
        // with the existing planner scorer. Frontend just displays them.
        var res = await mealPlanAPI.pairAlternatives(cookPlanId, 6);
        if (cancelled) return;
        setState(function (prev) { return prev && Object.assign({}, prev, { loading: false, candidates: res.data.alternatives || [] }); });
      } catch (err) {
        if (cancelled) return;
        setState(function (prev) { return prev && Object.assign({}, prev, { loading: false, error: 'load_failed' }); });
      }
    })();

    return function () { cancelled = true; };
  }, [context]);

  async function confirmPairSwap(newMealId) {
    if (!state) return;
    // Stable op id per swap attempt (target row + expected + replacement recipe):
    // reused across retries so an interrupted-but-committed swap is recognised as
    // an idempotent replay, never applied twice.
    var key = state.planId + ':' + (state.currentMealId || '') + ':' + newMealId;
    var opId;
    if (opRef.current && opRef.current.key === key) {
      opId = opRef.current.id;
    } else {
      opId = 'swappair_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      opRef.current = { key: key, id: opId };
    }
    setState(function (prev) { return prev && Object.assign({}, prev, { submitting: true }); });
    try {
      await mealPlanAPI.swapPair(state.planId, newMealId, {
        operationId: opId, expectedMealId: state.currentMealId,
        clientToday: getTodayString(),
      });
      opRef.current = null;
      // P8b-B1: pair Replace Both succeeded — ordered outgoing/incoming (one
      // signal per family even though two linked rows changed).
      recordSwapTasteSignals(state.currentMealId, newMealId);
      toast.success('Both linked meals replaced');
      if (onClose) onClose();
      if (onSwapped) onSwapped();
    } catch (err) {
      var detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(pairSwapErrorMessage(detail));
      setState(function (prev) { return prev && Object.assign({}, prev, { submitting: false }); });
    }
  }

  return (
    <Dialog open={!!context} onOpenChange={function (open) { if (!open && onClose) onClose(); }}>
      <DialogContent data-testid="pair-swap-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Replace both linked meals</DialogTitle>
          <DialogDescription data-testid="pair-swap-dates">
            {state && state.cookDate && state.leftoverDate
              ? ('Both the cook dinner (' + formatDate(state.cookDate) + ') and the next-day lunch (' + formatDate(state.leftoverDate) + ') will change to the recipe you pick.')
              : 'Both linked meals will change to the recipe you pick.'}
          </DialogDescription>
        </DialogHeader>

        {state && state.valid === false ? (
          <div className="py-4 text-center" data-testid="pair-swap-invalid">
            <p className="text-sm text-muted-foreground">
              This linked pair looks invalid, so it can't be swapped safely. Try clearing and regenerating the day.
            </p>
          </div>
        ) : state && state.loading ? (
          <div className="flex items-center justify-center py-8" data-testid="pair-swap-loading">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : state && state.error === 'load_failed' ? (
          <p className="py-4 text-sm text-muted-foreground text-center">Couldn't load replacement options. Please close and try again.</p>
        ) : state && state.candidates && state.candidates.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground text-center" data-testid="pair-swap-no-candidates">
            No eligible replacement recipe fits both days without changing your other meals.
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto py-2" data-testid="pair-swap-candidates">
            {(function () {
              // Backend flags each candidate that keeps BOTH linked days On Track.
              // Phase 2B also returns demoted candidates with a manual-choice
              // conflict (allergen, Don't Suggest, missing cookware, a recipe /
              // group already on either day) AFTER the clean ones: each shows
              // its conflicts and stays selectable. "Closest match" is judged on
              // the clean candidates only (the set this dialog always ranked):
              // when none of them keeps both days in range, the first clean one
              // is the closest match.
              var rows = annotatePairCandidates((state && state.candidates) || []);
              return rows.map(function (row) {
                var alt = row.candidate;
                var showKeeps = row.keeps;
                var showClosest = row.closest;
                var hasAllergen = row.conflicts.some(function (c) { return c.key === 'allergen'; });
                var rowTone = hasAllergen ? 'border-red-500/50 bg-red-500/5'
                  : (row.clean ? 'border-border/40' : 'border-amber-500/40');
                return (
                  <button
                    key={alt.meal_id}
                    onClick={function () { if (!state.submitting) confirmPairSwap(alt.meal_id); }}
                    disabled={state.submitting}
                    className={'w-full text-left p-3 rounded-xl border hover:border-accent hover:bg-accent/5 transition-colors disabled:opacity-50 ' + rowTone}
                    data-testid={'pair-swap-candidate-' + alt.meal_id}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{alt.meal_name}</span>
                      {row.conflicts.map(function (c) {
                        return (
                          <span key={c.key}
                            className={'px-2 py-0.5 rounded-full text-[10px] font-medium ' + conflictChipClass(c.tone)}
                            data-testid={'pair-swap-conflict-' + c.key + '-' + alt.meal_id}>
                            {conflictText(c)}
                          </span>
                        );
                      })}
                      {showKeeps && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-600" data-testid={'pair-swap-fit-keeps-' + alt.meal_id}>
                          Keeps both days in range
                        </span>
                      )}
                      {showClosest && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-600" data-testid={'pair-swap-fit-closest-' + alt.meal_id}>
                          Closest match
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {Math.round(alt.calories)} cal · {Math.round(alt.protein)}p · {Math.round(alt.carbs)}c · {Math.round(alt.fat)}f
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={function () { if (onClose) onClose(); }} data-testid="pair-swap-cancel-btn">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PairSwapDialog;
